import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { YouTubeEmbed } from "./YouTubeEmbed";
import { ImprovedFooterFour } from "./ImprovedFooterFour";
// import { FullscreenVideoModal } from "./FullscreenVideoModal";
import { useTranslation } from "react-i18next";

interface ShowData {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  startTime: string;
  duration: string;
  price: number;
  currency: string;
  min_participants: number;
  max_participants: number;
  min_charge_participants?: number | null;
  cut_off_days?: number;
  language: string;
  has_booking_conflict: boolean;
  isAvailable?: boolean;
  videos?: string[];
  images?: string[];
  faqs?: Array<{
    id: number;
    question: string | null;
    answer: string | null;
  }>;
  venue_info?: {
    name: string | null;
    description: string | null;
  };
}

interface SheetVariantFourProps {
  show: ShowData;
  date: Date;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBookNow: () => void;
  bookingType?: string;
}

export function SheetVariantFour({
  show,
  date,
  open,
  onOpenChange,
  onBookNow,
  bookingType,
}: SheetVariantFourProps) {
  const { t } = useTranslation();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl p-0 overflow-hidden"
      >
        <Tabs defaultValue="info" className="h-full flex flex-col">
          {/* Small screens (< 640px): Sticky header & footer layout */}
          <div className="h-full flex flex-col sm:hidden">
            {/* Sticky Header with Tabs */}
            <div className="shrink-0 border-b">
              <div className="p-4 pb-2">
                <SheetHeader className="space-y-0.5">
                  <SheetTitle className="text-sm leading-tight truncate">
                    {show.name.length > 25 ? `${show.name.slice(0, 25)}...` : show.name}
                  </SheetTitle>
                  <SheetDescription className="text-xs line-clamp-2">
                    {show.subtitle.length > 100 ? `${show.subtitle.slice(0, 100)}...` : show.subtitle}
                  </SheetDescription>
                </SheetHeader>
              </div>
              <TabsList className="grid w-full grid-cols-3 h-9 rounded-none border-0 bg-muted/30">
                <TabsTrigger
                  value="info"
                  className="text-xs rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                >
                  <span>📋</span>
                  <span>{t("booking.shows.sheet.info")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="faq"
                  className="text-xs rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                >
                  <span>❓</span>
                  <span>{t("booking.shows.sheet.faq")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="venue"
                  className="text-xs rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                >
                  <span>📍</span>
                  <span>{t("booking.shows.sheet.venue")}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Tab Content */}
              <TabsContent value="info" className="mt-0">
                <div className="pr-1">
                  {/* Video from API data */}
                  {show.videos && show.videos.length > 0 ? (
                    <div className="mb-3">
                      <video
                        controls
                        className="aspect-[3/1] w-full rounded-md"
                        poster={
                          show.images && show.images.length > 0
                            ? show.images[0]
                            : undefined
                        }
                      >
                        <source src={show.videos[0]} type="video/mp4" />
                        {t("booking.shows.sheet.videoNotSupported")}
                      </video>
                    </div>
                  ) : show.images && show.images.length > 0 ? (
                    <div className="mb-3">
                      <img
                        src={show.images[0]}
                        alt={show.name}
                        className="aspect-[3/1] w-full rounded-md object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="space-y-2 text-sm text-foreground pb-4">
                    <div className="space-y-1">
                      <h4 className="text-base font-medium text-foreground">
                        🎮 {t("booking.shows.sheet.aboutExperience")}
                      </h4>
                      <div
                        className="text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: show.description }}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="faq" className="mt-0">
                <div className="pr-1">
                  <div className="space-y-3">
                    <h3 className="text-base font-medium">
                      ❓ {t("booking.shows.sheet.frequentlyAskedQuestions")}
                    </h3>
                    <div className="space-y-4">
                      {show.faqs && show.faqs.length > 0 ? (
                        show.faqs.map((faq) => (
                          <div
                            key={faq.id}
                            className="p-3 rounded-lg border bg-muted/30"
                          >
                            <h4 className="text-sm font-semibold mb-2">
                              {faq.question}
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t("booking.shows.sheet.noFaqsAvailable")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="venue" className="mt-0">
                <div className="pr-1">
                  <div className="space-y-2">
                    <h3 className="text-base font-medium">
                      📍{" "}
                      {show.venue_info?.name ||
                        t("booking.shows.sheet.venueInformation")}
                    </h3>
                    <div className="space-y-1.5">
                      {show.venue_info?.description ? (
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {show.venue_info.description}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t("booking.shows.sheet.noVenueInfo", {
                            language:
                              show.language === "sv"
                                ? t("booking.shows.sheet.swedish")
                                : t("booking.shows.sheet.english"),
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>

            {/* Sticky Footer */}
            <div className="shrink-0 border-t bg-background p-2.5">
              <ImprovedFooterFour
                date={date}
                startTime={show.startTime}
                price={show.price}
                currency={show.currency}
                language={show.language || "en"}
                participants={`${show.min_participants}-${show.max_participants}`}
                duration={show.duration}
                isSoldOut={show.isAvailable === false}
                onBookNow={onBookNow}
                bookingType={bookingType}
                minChargeParticipants={show.min_charge_participants}
                cutOffDays={show.cut_off_days}
              />
            </div>
          </div>

          {/* Medium+ screens (≥ 640px): Sticky header & footer layout */}
          <div className="hidden sm:flex sm:flex-col h-full">
            {/* Sticky Header with Tabs */}
            <div className="shrink-0 border-b">
              <div className="p-6 pb-3">
                <SheetHeader className="space-y-0.5">
                  <SheetTitle className="text-base lg:text-lg leading-tight truncate">
                    {show.name.length > 25 ? `${show.name.slice(0, 25)}...` : show.name}
                  </SheetTitle>
                  <SheetDescription className="text-sm lg:text-base line-clamp-2">
                    {show.subtitle.length > 100 ? `${show.subtitle.slice(0, 100)}...` : show.subtitle}
                  </SheetDescription>
                </SheetHeader>
              </div>
              <TabsList className="grid w-full grid-cols-3 h-11 rounded-none border-0 bg-muted/30">
                <TabsTrigger
                  value="info"
                  className="text-sm rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                >
                  <span>📋</span>
                  <span>{t("booking.shows.sheet.info")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="faq"
                  className="text-sm rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                >
                  <span>❓</span>
                  <span>{t("booking.shows.sheet.faq")}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="venue"
                  className="text-sm rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=inactive]:text-foreground data-[state=inactive]:bg-background flex items-center justify-center gap-1"
                >
                  <span>📍</span>
                  <span>{t("booking.shows.sheet.venue")}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Tab Content */}
              <TabsContent value="info" className="mt-0">
                <div className="space-y-3">
                  {/* Video from API data */}
                  {show.videos && show.videos.length > 0 ? (
                    <div className="mb-4">
                      <video
                        controls
                        className="aspect-[3/1] w-full rounded-lg"
                        poster={
                          show.images && show.images.length > 0
                            ? show.images[0]
                            : undefined
                        }
                      >
                        <source src={show.videos[0]} type="video/mp4" />
                        {t("booking.shows.sheet.videoNotSupported")}
                      </video>
                    </div>
                  ) : show.images && show.images.length > 0 ? (
                    <div className="mb-4">
                      <img
                        src={show.images[0]}
                        alt={show.name}
                        className="aspect-[3/1] w-full rounded-lg object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="space-y-3 lg:text-base text-foreground">
                    <div className="space-y-2">
                      <h4 className="text-base lg:text-lg xl:text-xl font-medium text-foreground">
                        🎮 {t("booking.shows.sheet.aboutExperience")}
                      </h4>
                      <div
                        className="lg:text-base [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mt-2 [&_h3]:mb-1 [&_h4]:text-base [&_h4]:font-medium [&_h4]:mt-2 [&_h4]:mb-1 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-1 [&_p:has(br)]:mb-4 [&_br]:content-[''] [&_br]:block [&_strong]:font-semibold [&_em]:italic [&_span[style*='font-size']]:inline-block"
                        dangerouslySetInnerHTML={{ __html: show.description }}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="faq" className="mt-0">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base lg:text-lg xl:text-xl font-medium mb-4">
                      ❓ {t("booking.shows.sheet.frequentlyAskedQuestions")}
                    </h3>
                    <div className="space-y-5">
                      {show.faqs && show.faqs.length > 0 ? (
                        show.faqs.map((faq) => (
                          <div
                            key={faq.id}
                            className="p-4 rounded-lg border bg-muted/30"
                          >
                            <h4 className="lg:text-base font-semibold mb-2">
                              {faq.question}
                            </h4>
                            <p className="lg:text-base text-muted-foreground leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="lg:text-base text-muted-foreground">
                          {t("booking.shows.sheet.noFaqsAvailable")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="venue" className="mt-0">
                <div className="space-y-3">
                  <div>
                    <h3 className="text-base lg:text-lg xl:text-xl font-medium mb-3">
                      📍{" "}
                      {show.venue_info?.name ||
                        t("booking.shows.sheet.venueInformation")}
                    </h3>
                    <div className="space-y-2">
                      {show.venue_info?.description ? (
                        <p className="lg:text-base text-foreground whitespace-pre-wrap">
                          {show.venue_info.description}
                        </p>
                      ) : (
                        <p className="lg:text-base text-muted-foreground">
                          {t("booking.shows.sheet.noVenueInfo", {
                            language:
                              show.language === "sv"
                                ? t("booking.shows.sheet.swedish")
                                : t("booking.shows.sheet.english"),
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>

            {/* Sticky Footer */}
            <div className="shrink-0 border-t bg-background p-3">
              <ImprovedFooterFour
                date={date}
                startTime={show.startTime}
                price={show.price}
                currency={show.currency}
                language={show.language || "en"}
                participants={`${show.min_participants}-${show.max_participants}`}
                duration={show.duration}
                isSoldOut={show.isAvailable === false}
                onBookNow={onBookNow}
                bookingType={bookingType}
                minChargeParticipants={show.min_charge_participants}
                cutOffDays={show.cut_off_days}
              />
            </div>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// import { Show } from "@/lib/mockData";
// import {
//   Sheet,
//   SheetContent,
//   SheetHeader,
//   SheetTitle,
// } from "@/components/ui/sheet";
// import { Button } from "@/components/ui/button";

// interface SheetVariantFourProps {
//   show: Show;
//   date: Date;
//   open: boolean;
//   onOpenChange: (open: boolean) => void;
//   onBookNow: () => void;
// }

// export function SheetVariantFour({
//   show,
//   date,
//   open,
//   onOpenChange,
//   onBookNow
// }: SheetVariantFourProps) {
//   return (
//     <Sheet open={open} onOpenChange={onOpenChange}>
//       <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
//         <SheetHeader>
//           <SheetTitle>{show.name}</SheetTitle>
//         </SheetHeader>

//         <div className="space-y-4 py-4">
//           <img
//             src={show.image}
//             alt={show.name}
//             className="w-full h-48 object-cover rounded-lg"
//           />

//           <div className="space-y-2">
//             <h3 className="font-semibold">{show.subtitle}</h3>
//             <p className="text-sm text-muted-foreground">{show.description}</p>
//           </div>

//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="font-medium">Duration:</span> {show.duration}
//             </div>
//             <div>
//               <span className="font-medium">Group Size:</span> {show.groupSize}
//             </div>
//             <div>
//               <span className="font-medium">Difficulty:</span> {show.difficulty}
//             </div>
//             <div>
//               <span className="font-medium">Success Rate:</span> {show.successRate}
//             </div>
//           </div>

//           <div className="space-y-2">
//             <h4 className="font-medium">Features:</h4>
//             <ul className="text-sm space-y-1">
//               {show.features.slice(0, 5).map((feature, index) => (
//                 <li key={index} className="flex items-start gap-2">
//                   <span className="text-green-500">✓</span>
//                   {feature}
//                 </li>
//               ))}
//             </ul>
//           </div>
//         </div>

//         <div className="sticky bottom-0 bg-background border-t pt-4">
//           <Button
//             onClick={onBookNow}
//             className="w-full"
//             disabled={show.status === "soldout"}
//           >
//             {show.status === "soldout" ? "Sold Out" : `Book Now - ${show.price} ${show.currency}`}
//           </Button>
//         </div>
//       </SheetContent>
//     </Sheet>
//   );
// }
